import { Workbook, Worksheet } from 'exceljs';

import { TDatasetCellValue } from './getFormattedValue';

export interface SheetData {
  name: string;
  headers: string[];
  rows: TDatasetCellValue[][];
  columnFormats?: Array<string | undefined>;
}

export interface PerRowSecondaryData {
  mainRow: TDatasetCellValue[];
  secondarySheets: SheetData[];
}

export interface StackedSheetInput {
  name: string;
  headers: string[];
  columnFormats?: Array<string | undefined>;
  perRowData: PerRowSecondaryData[];
}

type SheetInput = SheetData | StackedSheetInput;

const isStacked = (sheet: SheetInput): sheet is StackedSheetInput =>
  'perRowData' in sheet;

const MIN_COL_WIDTH = 10;

const trimValue = (value: TDatasetCellValue): TDatasetCellValue =>
  typeof value === 'string' ? value.trim() : value;

const updateColWidths = (
  colWidths: number[],
  cells: Array<string | TDatasetCellValue>,
  columnFormats?: Array<string | undefined>
): void => {
  cells.forEach((cell, colIdx) => {
    const fmt = columnFormats?.[colIdx];
    const len =
      cell instanceof Date
        ? (fmt?.length ?? MIN_COL_WIDTH)
        : String(cell ?? '').trim().length;
    colWidths[colIdx] = Math.max(colWidths[colIdx] ?? MIN_COL_WIDTH, len);
  });
};

const addSection = (
  worksheet: Worksheet,
  {
    headers,
    rows,
    columnFormats,
  }: Pick<SheetData, 'headers' | 'rows' | 'columnFormats'>,
  colWidths: number[]
): void => {
  const headerRow = worksheet.addRow(headers);
  headerRow.font = { bold: true };
  updateColWidths(colWidths, headers, columnFormats);
  rows.forEach(row => {
    const excelRow = worksheet.addRow(row.map(trimValue));
    columnFormats?.forEach((fmt, colIdx) => {
      if (fmt) excelRow.getCell(colIdx + 1).numFmt = fmt;
    });
    updateColWidths(colWidths, row, columnFormats);
  });
};

export const downloadExcel = async (
  sheets: SheetInput[],
  filename: string
): Promise<void> => {
  const sanitizeSheetName = (name: string): string =>
    name.replace(/[*?:\\/[\]]/g, '').slice(0, 31);
  const workbook = new Workbook();
  sheets.forEach(sheet => {
    const worksheet = workbook.addWorksheet(sanitizeSheetName(sheet.name));
    const colWidths: number[] = [];
    if (isStacked(sheet)) {
      sheet.perRowData.forEach(({ mainRow, secondarySheets }, i) => {
        if (i > 0) worksheet.addRow([]);
        addSection(worksheet, { ...sheet, rows: [mainRow] }, colWidths);
        secondarySheets.forEach(sec => addSection(worksheet, sec, colWidths));
      });
    } else {
      addSection(worksheet, sheet, colWidths);
    }
    worksheet.columns.forEach((col, i) => {
      col.width = (colWidths[i] ?? MIN_COL_WIDTH) + 2;
    });
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const downloadLink = document.createElement('a');
  downloadLink.download = filename;
  downloadLink.href = URL.createObjectURL(blob);
  downloadLink.style.display = 'none';
  document.body.appendChild(downloadLink);
  downloadLink.click();
  URL.revokeObjectURL(downloadLink.href);
  downloadLink.remove();
};
